import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

// Mirror SERVICE_TIERS from server.py
const SERVICE_TIERS = {
  "student-onsite":  { label: "Student · On-Site",           needsAddress: false, prices: { 60: 200, 90: 250 } },
  "student-athome":  { label: "Student · At Home",           needsAddress: true,  prices: { 60: 250, 90: 300 } },
  "student-virtual": { label: "Student · Virtual",           needsAddress: false, prices: { 60: 125, 90: 165 } },
  "adult-onsite":    { label: "Adult · On-Site",             needsAddress: false, prices: { 60: 175, 90: 225 } },
  "adult-athome":    { label: "Adult · At Client Location",  needsAddress: true,  prices: { 60: 225, 90: 275 } },
  "adult-virtual":   { label: "Adult · Virtual",             needsAddress: false, prices: { 60: 110, 90: 145 } },
};

const emptyStudent = () => ({ student_name: "", esa_contract_number: "" });

export default function ManualBookingDialog({ open, onClose, onCreated }) {
  const [students, setStudents] = useState([emptyStudent()]);
  const [form, setForm] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    subject: "",
    service_type: "student-onsite",
    duration_minutes: 60,
    client_address: "",
    confirmed_slot_iso: "",
    travel_fee: 0,
    notes: "",
    status: "confirmed",
    payment_method: "classwallet",
    notify_parent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const tier = SERVICE_TIERS[form.service_type];
  const totalPrice = tier.prices[form.duration_minutes] ?? 0;
  const travel = Number(form.travel_fee) || 0;
  const grandTotal = totalPrice + travel;
  const n = students.length;
  const perStudentPrice = useMemo(() => (n > 0 ? (totalPrice / n).toFixed(2) : "0.00"), [totalPrice, n]);
  const perStudentTravel = useMemo(() => (n > 0 ? (travel / n).toFixed(2) : "0.00"), [travel, n]);
  const perStudentTotal = useMemo(
    () => (n > 0 ? ((totalPrice + travel) / n).toFixed(2) : "0.00"),
    [totalPrice, travel, n]
  );

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setStudentField = (i, k, v) =>
    setStudents((prev) => prev.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addStudent = () => {
    if (students.length >= 4) return;
    setStudents((prev) => [...prev, emptyStudent()]);
  };
  const removeStudent = (i) => {
    if (students.length === 1) return;
    setStudents((prev) => prev.filter((_, idx) => idx !== i));
  };
  const duplicateStudent = (i) => {
    if (students.length >= 4) return;
    setStudents((prev) => {
      const copy = [...prev];
      copy.splice(i + 1, 0, { ...prev[i], student_name: `${prev[i].student_name} (sibling)` });
      return copy;
    });
  };

  const submit = async () => {
    // Basic validation
    const cleanStudents = students
      .map((s) => ({
        student_name: (s.student_name || "").trim(),
        esa_contract_number: (s.esa_contract_number || "").trim(),
      }))
      .filter((s) => s.student_name);
    if (cleanStudents.length === 0) {
      toast.error("Add at least one student name");
      return;
    }
    if (!form.parent_name.trim() || !form.parent_email.trim()) {
      toast.error("Parent name and email are required");
      return;
    }
    if (tier.needsAddress && !form.client_address.trim()) {
      toast.error("Client address is required for at-home bookings");
      return;
    }
    let confirmed_slot_iso = null;
    if (form.confirmed_slot_iso) {
      // datetime-local -> ISO
      const d = new Date(form.confirmed_slot_iso);
      if (!isNaN(d.getTime())) confirmed_slot_iso = d.toISOString();
    }

    setSubmitting(true);
    try {
      const payload = {
        students: cleanStudents,
        parent_name: form.parent_name.trim(),
        parent_email: form.parent_email.trim().toLowerCase(),
        parent_phone: form.parent_phone.trim(),
        subject: form.subject.trim(),
        service_type: form.service_type,
        duration_minutes: Number(form.duration_minutes),
        client_address: form.client_address.trim(),
        confirmed_slot_iso,
        travel_fee: travel,
        notes: form.notes.trim(),
        status: form.status,
        payment_method: form.payment_method,
        notify_parent: form.notify_parent,
      };
      const { data } = await api.post("/admin/bookings/manual", payload);
      const count = data.booking_ids?.length || 1;
      toast.success(`Created ${count} booking${count > 1 ? "s" : ""}`);
      if (data.stripe_checkout_url) {
        try {
          await navigator.clipboard.writeText(data.stripe_checkout_url);
          toast.success("Stripe payment link copied to clipboard");
        } catch {
          /* ignore clipboard errors */
        }
      }
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-booking-dialog">
        <DialogHeader>
          <DialogTitle>Add Manual 1-on-1 Booking</DialogTitle>
          <DialogDescription>
            Create a booking from a phone/email intake. Add 1–4 students to split fees evenly across siblings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Students */}
          <section className="rounded-lg border border-gg-border bg-gg-bg/40 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gg-brown">
                  Students {n > 1 && <span className="text-gg-muted normal-case font-normal">(fees split evenly)</span>}
                </h3>
                <p className="text-xs text-gg-muted">1–4 siblings. Each gets their own invoice with their own ESA ID.</p>
              </div>
              <button
                type="button"
                onClick={addStudent}
                disabled={students.length >= 4}
                className="btn-pill btn-outline text-xs disabled:opacity-50"
                data-testid="manual-booking-add-student"
              >
                <Plus size={14} /> Add student
              </button>
            </div>
            {students.map((s, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div>
                  <Label htmlFor={`student-name-${i}`} className="text-xs">Student name {i + 1}</Label>
                  <Input
                    id={`student-name-${i}`}
                    value={s.student_name}
                    onChange={(e) => setStudentField(i, "student_name", e.target.value)}
                    placeholder="Full name"
                    data-testid={`manual-booking-student-name-${i}`}
                  />
                </div>
                <div>
                  <Label htmlFor={`student-esa-${i}`} className="text-xs">ESA ID # (optional)</Label>
                  <Input
                    id={`student-esa-${i}`}
                    value={s.esa_contract_number}
                    onChange={(e) => setStudentField(i, "esa_contract_number", e.target.value)}
                    placeholder="ESA-XXXX-XXXX"
                    data-testid={`manual-booking-student-esa-${i}`}
                  />
                </div>
                <div className="flex gap-1 pb-1">
                  <button
                    type="button"
                    onClick={() => duplicateStudent(i)}
                    disabled={students.length >= 4}
                    title="Duplicate for sibling"
                    className="btn-icon btn-outline disabled:opacity-40"
                    data-testid={`manual-booking-dup-student-${i}`}
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeStudent(i)}
                    disabled={students.length === 1}
                    title="Remove"
                    className="btn-icon btn-outline text-red-600 disabled:opacity-40"
                    data-testid={`manual-booking-remove-student-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Parent */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="mb-parent-name">Parent name *</Label>
              <Input
                id="mb-parent-name"
                value={form.parent_name}
                onChange={(e) => setField("parent_name", e.target.value)}
                data-testid="manual-booking-parent-name"
              />
            </div>
            <div>
              <Label htmlFor="mb-parent-email">Parent email *</Label>
              <Input
                id="mb-parent-email"
                type="email"
                value={form.parent_email}
                onChange={(e) => setField("parent_email", e.target.value)}
                data-testid="manual-booking-parent-email"
              />
            </div>
            <div>
              <Label htmlFor="mb-parent-phone">Parent phone</Label>
              <Input
                id="mb-parent-phone"
                value={form.parent_phone}
                onChange={(e) => setField("parent_phone", e.target.value)}
                data-testid="manual-booking-parent-phone"
              />
            </div>
            <div>
              <Label htmlFor="mb-subject">Subject / topic</Label>
              <Input
                id="mb-subject"
                value={form.subject}
                onChange={(e) => setField("subject", e.target.value)}
                placeholder="e.g. Chicken care basics"
                data-testid="manual-booking-subject"
              />
            </div>
          </section>

          {/* Service tier */}
          <section className="rounded-lg border border-gg-border bg-gg-bg/40 p-4 space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gg-brown">Service</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="mb-service">Service tier</Label>
                <select
                  id="mb-service"
                  value={form.service_type}
                  onChange={(e) => setField("service_type", e.target.value)}
                  className="w-full border border-gg-border rounded-md p-2 bg-white"
                  data-testid="manual-booking-service-type"
                >
                  {Object.entries(SERVICE_TIERS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="mb-duration">Duration</Label>
                <select
                  id="mb-duration"
                  value={form.duration_minutes}
                  onChange={(e) => setField("duration_minutes", Number(e.target.value))}
                  className="w-full border border-gg-border rounded-md p-2 bg-white"
                  data-testid="manual-booking-duration"
                >
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                </select>
              </div>
              <div>
                <Label>Base price</Label>
                <div className="p-2 rounded-md bg-white border border-gg-border font-semibold" data-testid="manual-booking-base-price">
                  ${totalPrice.toFixed(2)}
                </div>
              </div>
            </div>
            {tier.needsAddress && (
              <div>
                <Label htmlFor="mb-address">Client address *</Label>
                <Input
                  id="mb-address"
                  value={form.client_address}
                  onChange={(e) => setField("client_address", e.target.value)}
                  placeholder="Street, City, AZ"
                  data-testid="manual-booking-address"
                />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mb-confirmed-slot">Confirmed date/time (optional)</Label>
                <Input
                  id="mb-confirmed-slot"
                  type="datetime-local"
                  value={form.confirmed_slot_iso}
                  onChange={(e) => setField("confirmed_slot_iso", e.target.value)}
                  data-testid="manual-booking-slot"
                />
                <p className="text-xs text-gg-muted mt-1">Leave blank to save as a pending request.</p>
              </div>
              <div>
                <Label htmlFor="mb-travel">Travel fee</Label>
                <Input
                  id="mb-travel"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.travel_fee}
                  onChange={(e) => setField("travel_fee", e.target.value)}
                  data-testid="manual-booking-travel-fee"
                />
              </div>
            </div>
          </section>

          {/* Payment */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="mb-payment">Payment method</Label>
              <select
                id="mb-payment"
                value={form.payment_method}
                onChange={(e) => setField("payment_method", e.target.value)}
                className="w-full border border-gg-border rounded-md p-2 bg-white"
                data-testid="manual-booking-payment-method"
              >
                <option value="classwallet">ClassWallet (invoice)</option>
                <option value="direct">Direct Pay (Stripe link)</option>
              </select>
            </div>
            <div>
              <Label htmlFor="mb-status">Booking status</Label>
              <select
                id="mb-status"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                className="w-full border border-gg-border rounded-md p-2 bg-white"
                data-testid="manual-booking-status"
              >
                <option value="confirmed">Confirmed</option>
                <option value="requested">Requested (pending)</option>
                <option value="pending_admin_review">Pending admin review</option>
              </select>
            </div>
          </section>

          <div>
            <Label htmlFor="mb-notes">Admin notes</Label>
            <Textarea
              id="mb-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              data-testid="manual-booking-notes"
            />
          </div>

          <label className="flex items-center gap-2 text-sm" data-testid="manual-booking-notify-label">
            <input
              type="checkbox"
              checked={form.notify_parent}
              onChange={(e) => setField("notify_parent", e.target.checked)}
              data-testid="manual-booking-notify"
            />
            Send parent confirmation email {form.payment_method === "direct" && "(includes Stripe pay link)"}
          </label>

          {/* Summary */}
          <div className="rounded-lg border border-gg-primary/30 bg-gg-primary/5 p-4 text-sm" data-testid="manual-booking-summary">
            <div className="flex justify-between">
              <span>Base price</span>
              <span>${totalPrice.toFixed(2)}</span>
            </div>
            {travel > 0 && (
              <div className="flex justify-between">
                <span>Travel fee</span>
                <span>${travel.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold border-t border-gg-primary/20 pt-2 mt-2">
              <span>Total</span>
              <span data-testid="manual-booking-grand-total">${grandTotal.toFixed(2)}</span>
            </div>
            {n > 1 && (
              <div className="mt-3 pt-3 border-t border-gg-primary/20 text-xs text-gg-muted space-y-0.5">
                <div className="font-semibold text-gg-brown">Per-student split (× {n}):</div>
                <div className="flex justify-between">
                  <span>Price / student</span>
                  <span data-testid="manual-booking-per-price">${perStudentPrice}</span>
                </div>
                {travel > 0 && (
                  <div className="flex justify-between">
                    <span>Travel / student</span>
                    <span>${perStudentTravel}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-gg-fg">
                  <span>Invoice total / student</span>
                  <span data-testid="manual-booking-per-total">${perStudentTotal}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="btn-pill btn-outline"
            data-testid="manual-booking-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="btn-pill btn-primary disabled:opacity-50"
            data-testid="manual-booking-submit"
          >
            {submitting ? "Creating..." : `Create ${n > 1 ? `${n} bookings` : "booking"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
